"use client"

import { t as tNow, useT } from '@/lib/i18n'
import { useEffect, useState } from 'react'
import { Check, Copy, FileText, Landmark, Loader2, Send, CheckCircle2, CalendarClock } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useStore } from '@/lib/store'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { useMediaQuery, PHONE_QUERY } from '@/hooks/use-media-query'
import {
  payCode, payPurpose, priceFor, groupIban, compactIban, subscriptionState, periodFor, geDate, geDateTime,
  buildSubInvoice, subInvoiceHtml, type SubState,
} from '@/lib/subscription'
import { printA4 } from '@/lib/invoice'
import { isPlayInstall } from '@/lib/channel'

/**
 * 097 — how a shop pays for MartePOS: the period, the amount, the accounts,
 * the purpose line with the shop's number, and „I transferred".
 *
 * One panel, three doors: the settings row „გამოწერა", the banner above every
 * page when the date is near, and the lock screen of a shop that is already
 * closed — the one that most needs it. The requisites are read from the
 * database every time the panel opens (the platform admin can change them
 * without a deploy); the amount is shown here and computed again by the
 * database when the owner presses „გადავრიცხე".
 */

/** One short line for a list row or a banner. */
export function subShort(st: SubState): string {
  switch (st.kind) {
    case 'trial': return tNow('bill.short.trial', { n: st.daysLeft })
    case 'trial-grace': return tNow('bill.short.grace')
    case 'trial-locked': return tNow('bill.short.locked')
    case 'paid': return tNow('bill.short.until', { date: geDate(st.until) })
    case 'due-soon': return tNow('bill.short.until', { date: geDate(st.until) })
    case 'overdue': return tNow('bill.short.overdue')
    case 'none': return tNow('bill.short.none')
  }
}

function statusLine(st: SubState): { title: string; tone: 'ok' | 'warn' | 'bad' | 'info' } {
  switch (st.kind) {
    case 'trial': return { title: tNow('bill.st.trial', { n: st.daysLeft }), tone: 'info' }
    case 'trial-grace': return { title: tNow('bill.st.grace', { n: st.lockInDays }), tone: 'bad' }
    case 'trial-locked': return { title: tNow('bill.st.locked'), tone: 'bad' }
    case 'paid': return { title: tNow('bill.st.paid', { date: geDate(st.until) }), tone: 'ok' }
    case 'due-soon': return { title: tNow('bill.st.dueSoon', { date: geDate(st.until), n: st.daysLeft }), tone: 'warn' }
    case 'overdue': return { title: tNow('bill.st.overdue', { n: st.daysOver }), tone: 'bad' }
    case 'none': return { title: tNow('bill.st.none'), tone: 'ok' }
  }
}

const TONE: Record<'ok' | 'warn' | 'bad' | 'info', string> = {
  ok: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25',
  warn: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30',
  bad: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/25',
  info: 'bg-primary/10 text-primary border-primary/25',
}

function CopyRow({ label, value, display, mono }: { label: string; value: string; display?: string; mono?: boolean }) {
  const t = useT()
  const [done, setDone] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setDone(true)
      setTimeout(() => setDone(false), 1400)
    } catch {
      toast.error(t('bill.pay.1'))
    }
  }
  return (
    <div className="flex items-center gap-3 px-3.5 py-2.5">
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-semibold text-muted-foreground">{label}</p>
        <p className={cn('font-bold text-foreground break-words select-all', mono ? 'font-mono text-[14px] sm:text-[15px]' : 'text-[15px]')}>{display ?? value}</p>
      </div>
      <button
        type="button"
        onClick={copy}
        aria-label={t('bill.pay.2', { label: label })}
        className={cn(
          'shrink-0 h-10 min-w-10 px-2.5 sm:px-3 rounded-xl border text-[13px] font-bold flex items-center justify-center gap-1.5 transition-colors',
          done ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-border bg-card hover:bg-muted/60 text-foreground',
        )}
      >
        {done ? <Check className="size-4" /> : <Copy className="size-4" />}
        <span className="hidden sm:inline">{done ? t('bill.pay.3') : t('bill.pay.4')}</span>
      </button>
    </div>
  )
}

export function PayPanel() {
  const t = useT()
  const org = useStore((s) => s.currentOrg)
  const settings = useStore((s) => s.settings)
  const billing = useStore((s) => s.platformBilling)
  const loadBilling = useStore((s) => s.loadPlatformBilling)
  const claimPayment = useStore((s) => s.claimPayment)
  const isOwner = useStore((s) => s.userRole === 'owner' && !s.supportSession)

  const [months, setMonths] = useState<1 | 12>(1)
  const [asking, setAsking] = useState(false)       // „which account?" row open
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(!!billing)

  useEffect(() => {
    let alive = true
    void loadBilling().then(() => { if (alive) setLoaded(true) })
    return () => { alive = false }
  }, [loadBilling])

  if (!org) return null
  const st = subscriptionState(org)
  const line = statusLine(st)
  const code = payCode(org.billing_no)

  // The Google Play build: where the shop stands — and nothing about how to
  // pay, not even the reference code (lib/channel.ts, Play's payments policy).
  if (isPlayInstall()) {
    return (
      <div className="flex flex-col gap-3">
        <div className={cn('rounded-2xl border px-4 py-3 flex items-center gap-3', TONE[line.tone])}>
          <CalendarClock className="size-5 shrink-0" />
          <p className="flex-1 min-w-0 text-sm font-bold leading-snug">{line.title}</p>
        </div>
        <p className="text-[13px] text-muted-foreground">{t('bill.pay.5')}</p>
      </div>
    )
  }

  if (!loaded && !billing) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center"><Loader2 className="size-4 animate-spin" />{t('bill.pay.6')}</div>
  }
  const accounts = billing?.accounts ?? []
  const price = billing ? priceFor(billing, org.custom_price_month, 1) : 0
  const yearPrice = billing ? priceFor(billing, org.custom_price_month, 12) : 0
  const amount = months === 12 ? yearPrice : price
  const { from, to } = periodFor(org, months)
  const lastDay = new Date(to.getTime() - 86_400_000)
  const claimed = org.pay_claim_at

  const send = async (bank: string | null) => {
    setBusy(true)
    const ok = await claimPayment(months, bank)
    setBusy(false)
    setAsking(false)
    if (ok) toast.success(t('bill.pay.7'))
  }

  const onClaim = () => {
    if (accounts.length > 1) setAsking(true)
    else void send(accounts[0]?.bank ?? null)
  }

  const printPaper = () => {
    if (!billing) return
    const ok = printA4(subInvoiceHtml(buildSubInvoice(billing, { ...org, name: org.name }, settings, months)))
    if (!ok) toast.error(t('common.popupBlocked'))
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Where the shop stands, and its number */}
      <div className={cn('rounded-2xl border px-4 py-3 flex items-center gap-3', TONE[line.tone])}>
        <CalendarClock className="size-5 shrink-0" />
        <p className="flex-1 min-w-0 text-sm font-bold leading-snug">{line.title}</p>
        {code && <span className="shrink-0 rounded-lg bg-background/70 dark:bg-black/20 px-2 py-1 font-mono text-[13px] font-bold text-foreground">{code}</span>}
      </div>

      {accounts.length === 0 ? (
        <p className="text-sm text-muted-foreground rounded-2xl border border-dashed border-border p-4">
          {t('bill.pay.8')}
        </p>
      ) : (
        <>
          {/* The period */}
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('bill.pay.9')}>
            {([
              { m: 1 as const, title: t('bill.pay.10'), sum: price, sub: t('bill.pay.11') },
              { m: 12 as const, title: t('bill.pay.12'), sum: yearPrice, sub: yearPrice < price * 12 ? t('bill.pay.13', { n: Math.round(12 - yearPrice / price) }) : t('bill.pay.14') },
            ]).map((o) => (
              <button
                key={o.m}
                type="button"
                role="radio"
                aria-checked={months === o.m}
                onClick={() => setMonths(o.m)}
                className={cn(
                  'rounded-2xl border-2 px-3.5 py-3 text-left transition-colors',
                  months === o.m ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/40',
                )}
              >
                <span className="block text-[13px] font-bold text-muted-foreground">{o.title}</span>
                <span className="block text-2xl font-black tabular-nums text-foreground">₾{o.sum.toFixed(0)}</span>
                <span className={cn('block text-[12px] font-semibold', months === o.m ? 'text-primary' : 'text-muted-foreground')}>{o.sub}</span>
              </button>
            ))}
          </div>
          <p className="text-[13px] text-muted-foreground -mt-1 px-1">
            {t('bill.pay.15')}<span className="font-semibold text-foreground">{geDate(from)} – {geDate(lastDay)}</span>
          </p>

          {/* Where to pay */}
          <div className="rounded-2xl border border-border bg-card dark:bg-white/[0.03] divide-y divide-border overflow-hidden">
            <div className="px-3.5 py-2.5 flex items-center gap-2">
              <Landmark className="size-4 text-primary shrink-0" />
              <p className="text-[13px] font-bold text-foreground">{t('bill.pay.16')}</p>
            </div>
            <CopyRow label={t('bill.pay.17')} value={billing?.recipient ?? ''} />
            {accounts.map((a) => (
              <CopyRow key={a.iban} label={a.bank} value={compactIban(a.iban)} display={groupIban(a.iban)} mono />
            ))}
            <CopyRow label={t('bill.pay.18')} value={amount.toFixed(2)} display={`₾${amount.toFixed(2)}`} />
            <CopyRow label={t('bill.pay.19')} value={payPurpose(org.billing_no)} mono />
          </div>
          <p className="text-[12px] text-muted-foreground leading-relaxed px-1 -mt-1">
            {t('bill.pay.20')}<span className="font-mono font-bold text-foreground">{code}</span>{t('bill.pay.21')}
            {billing?.note ? ` ${billing.note}` : ''}
          </p>

          {/* After the transfer */}
          {claimed && (
            <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 flex gap-3">
              <CheckCircle2 className="size-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800 dark:text-emerald-200 leading-snug">
                <span className="font-bold">{t('bill.pay.22')}</span> {geDateTime(claimed)}
                {org.pay_claim_amount ? ` · ₾${org.pay_claim_amount.toFixed(2)}` : ''}{t('bill.pay.23')}
              </p>
            </div>
          )}

          {asking ? (
            <div className="rounded-2xl border border-border p-3 flex flex-col gap-2">
              <p className="text-[13px] font-bold text-foreground px-1">{t('bill.pay.24')}</p>
              <div className="grid grid-cols-2 gap-2">
                {accounts.map((a) => (
                  <button
                    key={a.iban}
                    type="button"
                    disabled={busy}
                    onClick={() => send(a.bank)}
                    className="h-12 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {busy ? <Loader2 className="size-4 animate-spin" /> : null}{a.bank}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setAsking(false)} className="h-10 text-sm font-semibold text-muted-foreground hover:text-foreground">{t('common.cancel')}</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2">
              <button
                type="button"
                onClick={printPaper}
                className="h-12 px-4 rounded-xl border border-border bg-card hover:bg-muted/60 text-sm font-bold text-foreground flex items-center justify-center gap-2"
              >
                <FileText className="size-4" />{t('bill.pay.25')}
              </button>
              {isOwner ? (
                <button
                  type="button"
                  onClick={onClaim}
                  disabled={busy}
                  className={cn(
                    'h-12 px-4 rounded-xl text-sm font-black flex items-center justify-center gap-2 disabled:opacity-60',
                    claimed ? 'border border-border bg-card hover:bg-muted/60 text-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90',
                  )}
                >
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                  {claimed ? t('bill.pay.26') : t('bill.pay.27', { amount: amount.toFixed(2) })}
                </button>
              ) : (
                <p className="text-[13px] text-muted-foreground self-center px-1">{t('bill.pay.28')}</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** The panel in a dialog (desktop) or a bottom sheet (phone). */
export function PayDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const t = useT()
  const isPhone = useMediaQuery(PHONE_QUERY)
  if (isPhone) {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange}>
        <BottomSheetContent title={t('bill.pay.29')} className="max-h-[94dvh]">
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 pt-1 pb-10">
            {open && <PayPanel />}
          </div>
        </BottomSheetContent>
      </BottomSheet>
    )
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-[24px] p-6 max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-black">{t('bill.pay.29')}</DialogTitle>
        </DialogHeader>
        {open && <PayPanel />}
      </DialogContent>
    </Dialog>
  )
}
